import {
  AutocompleteRenderOptionState,
  Checkbox,
  ListItemButton,
} from "@mui/material";
import { HTMLAttributes, ReactElement } from "react";

import { ListTenantsWithSubaccountsQuery } from "~frontend/__generated__/graphql";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";

import { SearchableSelect, SearchableSelectProps } from "./FormSelect";

type SearchableSelectAnyProps = SearchableSelectProps<string>;

interface TenantDropdownSingleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  value?: string;
  onChange?: (tenantId?: string) => void | Promise<void>;
  multiple?: false;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface TenantDropdownMultipleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  value?: string[];
  onChange?: (tenantIds: string[]) => void | Promise<void>;
  multiple: true;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export type TenantDropdownProps =
  | TenantDropdownSingleProps
  | TenantDropdownMultipleProps;

export function TenantDropdown(props: TenantDropdownProps): ReactElement {
  const { tenants, label = "STW-T Tenant", placeholder, disabled } = props;

  const tenantOptions = tenants.map(tenant => ({
    label: `${tenant.name} (${tenant.slug})`,
    id: tenant.id,
  }));

  const searchableSelectProps = {
    options: tenantOptions,
    label,
    placeholder,
    disabled,
    value: props.value,
    onChange: props.onChange,
    ...(props.multiple && {
      multiple: true as const,
      renderOption: (
        itemProps: HTMLAttributes<HTMLLIElement> & { key: any },
        option: { label: string; id: string },
        { selected }: AutocompleteRenderOptionState
      ) => {
        const { key, ...otherProps } = itemProps;
        return (
          <ListItemButton component="li" key={key} {...otherProps}>
            <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
            {option.label}
          </ListItemButton>
        );
      },
    }),
  } as SearchableSelectAnyProps;

  return <SearchableSelect<string> {...searchableSelectProps} />;
}

interface SubaccountDropdownSingleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  selectedTenantId?: string;
  value?: string;
  onChange?: (subaccountId?: string) => void | Promise<void>;
  multiple?: false;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface SubaccountDropdownMultipleProps {
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  selectedTenantId?: string;
  value?: string[];
  onChange?: (subaccountIds: string[]) => void | Promise<void>;
  multiple: true;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export type SubaccountDropdownProps =
  | SubaccountDropdownSingleProps
  | SubaccountDropdownMultipleProps;

export function SubaccountDropdown(
  props: SubaccountDropdownProps
): ReactElement {
  const {
    tenants,
    selectedTenantId,
    label = "STW-T Subaccount",
    placeholder,
    disabled,
  } = props;

  const tenant = tenants.find(t => t.id === selectedTenantId);
  const subaccountOptions =
    tenant?.subaccounts.map(subaccount => ({
      label: `${subaccount.name} (${subaccount.slug})`,
      id: subaccount.id,
    })) || [];

  const searchableSelectProps = {
    options: subaccountOptions,
    label,
    placeholder,
    disabled,
    value: props.value,
    onChange: props.onChange,
    ...(props.multiple && {
      multiple: true as const,
      renderOption: (
        itemProps: HTMLAttributes<HTMLLIElement> & { key: any },
        option: { label: string; id: string },
        { selected }: AutocompleteRenderOptionState
      ) => {
        const { key, ...otherProps } = itemProps;
        return (
          <ListItemButton component="li" key={key} {...otherProps}>
            <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
            {option.label}
          </ListItemButton>
        );
      },
    }),
  } as SearchableSelectAnyProps;

  return <SearchableSelect<string> {...searchableSelectProps} />;
}
